import React, { useState, useMemo, useCallback, useRef, KeyboardEvent, useEffect } from 'react';
import {
    StyledDropdownContainer,
    StyledDropdownTrigger,
    StyledDropdownTitle,
    StyledDropdownList,
    StyledListItem,
    StyledListOption,
    StyledDropdownTriggerAsSecondaryButton,
} from './styled';
import { useClickAway, useMedia } from 'react-use';
import ChevronDown from '$icons/chevron-down.svg';
import ChevronUp from '$icons/chevron-up.svg';
import { ButtonPropsLegacy, LineSpinner } from '$shared/components';
import { queries } from '$theme';

export type DropdownItem = {
    value: string | number;
    label: string;
};

export interface DropdownProps {
    items: DropdownItem[];
    value: string | number;
    onChange: (value: string | number) => void;
    name: string;
    asSecondaryButton?: boolean;
    isLoading?: boolean;
}

export const Dropdown = ({
    items,
    value,
    onChange,
    name,
    asSecondaryButton,
    isLoading,
}: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [buttonStyle, setButtonStyle] = useState({ size: 'lg' } as ButtonPropsLegacy);
    const isMobile = !useMedia(queries.md, false);

    const currentLabel = useMemo(() => {
        return items?.find((item) => item.value === value)?.label || '';
    }, [items, value]);

    const setValue = useCallback(
        (value: string | number) => {
            onChange(value);
        },
        [onChange]
    );

    useEffect(() => {
        setButtonStyle({ size: isMobile ? 'md' : 'lg' } as ButtonPropsLegacy);
    }, [isMobile, setButtonStyle]);

    useClickAway(
        dropdownRef,
        (e: MouseEvent) => {
            if (dropdownRef?.current?.contains(e.target as HTMLElement)) return;
            setIsOpen(false);
        },
        ['click']
    );

    if (!items.length) return null;

    // Event handler for keydowns
    const handleKeyDown = (e: KeyboardEvent, selectedValue?: string | number) => {
        const selectedIndex = items.findIndex((item) => item.value == value);

        switch (e.key) {
            case 'Tab':
                setIsOpen(false);
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;
            case 'Home':
                if (dropdownRef?.current !== undefined) {
                    (dropdownRef.current?.querySelector(
                        'li:first-of-type span'
                    ) as HTMLElement)?.focus();
                }
                break;
            case 'End':
                if (dropdownRef?.current !== undefined) {
                    (dropdownRef.current?.querySelector(
                        'li:last-of-type span'
                    ) as HTMLElement)?.focus();
                }
                break;
            case 'ArrowDown':
                if (!isOpen) {
                    setIsOpen(true);
                }
                if (selectedIndex == -1) {
                    setValue(items[0].value);
                } else if (items.length > selectedIndex + 1) {
                    setValue(items[selectedIndex + 1].value);
                }
                break;
            case 'ArrowUp':
                if (!isOpen) {
                    setIsOpen(true);
                }
                if (selectedIndex == -1) {
                    setValue(items[0].value);
                } else if (0 <= selectedIndex - 1) {
                    setValue(items[selectedIndex - 1].value);
                }
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedValue) {
                    setValue(selectedValue);
                }
                setIsOpen(false);
                break;
            default:
                break;
        }
    };

    return (
        <StyledDropdownContainer ref={dropdownRef}>
            {!asSecondaryButton ? (
                <StyledDropdownTrigger
                    isOpen={isOpen}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-labelledby={name}
                    onClick={() => setIsOpen(!isOpen)}
                    onKeyDown={handleKeyDown}
                >
                    <StyledDropdownTitle id={name}>{currentLabel}</StyledDropdownTitle>
                    {!isOpen ? (
                        <ChevronDown aria-hidden="true" />
                    ) : (
                        <ChevronUp aria-hidden="true" />
                    )}
                </StyledDropdownTrigger>
            ) : (
                <StyledDropdownTriggerAsSecondaryButton
                    isOpen={isOpen}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-labelledby={name}
                    onClick={() => setIsOpen(!isOpen)}
                    onKeyDown={handleKeyDown}
                    isLoading={isLoading}
                    variant="secondary"
                    {...buttonStyle}
                >
                    {currentLabel}
                    {!isOpen ? (
                        <ChevronDown aria-hidden="true" />
                    ) : (
                        <ChevronUp aria-hidden="true" />
                    )}
                    {isLoading ? <LineSpinner shade="dark" /> : null}
                </StyledDropdownTriggerAsSecondaryButton>
            )}
            {isOpen ? (
                <StyledDropdownList
                    role="listbox"
                    aria-activedescendant={`${name}-${value}`}
                    aria-labelledby={name}
                    tabIndex={-1}
                >
                    {items.map((item) => (
                        <StyledListItem
                            id={item.value.toString()}
                            role="option"
                            aria-selected={value === item.value}
                            key={item.value}
                            aria-labelledby={item.value.toString()}
                            onClick={() => {
                                setValue(item.value);
                                setIsOpen(false);
                            }}
                        >
                            <StyledListOption
                                isSelected={value === item.value}
                                id={`${name}-${item.value}`}
                                aria-label={item.value.toString()}
                                onKeyDown={(e) => handleKeyDown(e, item.value)}
                                tabIndex={0}
                            >
                                {item.label}
                            </StyledListOption>
                        </StyledListItem>
                    ))}
                </StyledDropdownList>
            ) : null}
        </StyledDropdownContainer>
    );
};
